import React, { useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import { FormProvider, useForm } from 'react-hook-form'
import { useDispatch } from 'react-redux'
import { UserApi } from 'mvppoker-portal-common/api'
import { useNavigate } from '../../navigation'
import { MVPButton } from '../../../Views/Buttons/MVPButton'
import { FullPageHeader } from '../../../Views/FullPageHeader/FullPageHeader'
import { MVPLink } from '../../../Views/Link/MVPLink'
import { MVPTextInput } from '../../../Views/TextInput/MVPTextInput'
import { RegisteringUser, setCredentialsAndUser } from '../../../state/reducers/registeringUser'
import { handleError, useApi } from '../../../utils/useApi'
import './RegistrationCredentialsPage.scss'

type RegistrationCredentialsFormData = Required<RegisteringUser>['credentials']

export default () => {
	const form = useForm<RegistrationCredentialsFormData>()
	const goToNextPage = useNavigate('registrationConfirmationEmail')
	const goToLoginPage = useNavigate('login')
	const dispatch = useDispatch()
	const [isLoading, setLoading] = useState(false)
	const userApi = useApi(UserApi)
	const [searchParams] = useSearchParams()
	const referralCode = searchParams.get('referralCode')

	const onSubmit = (credentials: RegistrationCredentialsFormData) => {
		setLoading(true)

		const registerAsync = async () => {
			const data = 
				await userApi.authUserRegister({
					username: credentials.email,
					password: credentials.password,
					name: credentials.name,
					referralCode: credentials.referral,
				})

			dispatch(
				setCredentialsAndUser({
					credentials,
					user: data.data,
				}),
			)

			goToNextPage()
		}
		registerAsync().catch((err) => {
			setLoading(false)
			handleError(err)
		})
	}

	return (
		<div id='registration-credentials-page' className='stretch-page'>
			<div className='middle-col'>
				<FullPageHeader title='registration' />
				<FormProvider {...form}>
					<form id='registration-credentials-page' onSubmit={form.handleSubmit(onSubmit)}>
						<div className='registration-form-grid'>
							<MVPTextInput formField='name' formValidation={{ required: 'Name required!' }} label='Name' placeholder='Enter your name' type='text' />
							<MVPTextInput
								formField='email'
								formValidation={{
									required: 'email required!',
								}}
								label='Email'
								placeholder='Enter your email'
								type='email'
							/>
							<MVPTextInput
								formField='password'
								formValidation={{
									required: 'Password required!',
									validate: {
										has8Chars: (it: string) => it.length >= 8 || 'Minimum 8 characters',
										hasLessThan100Char: (it: string) => it.length < 100 || 'Maximum 8 characters',
										has1Uppercase: (it: string) => !![...it].find((it) => it >= 'A' && it <= 'Z') || 'Needs one uppercase letter',
										has1Number: (it: string) => !![...it].find((it) => it >= '0' && it <= '9') || 'Needs one number',
										has1SpecialChar: (it: string) =>
											!![...it].find((it) => '^$*.[]{}()?-"!@#%&/\\,><\':;|_~`+='.includes(it)) ||
											'Needs at least one special character (^$*.[]{}()?-"!@#%&/\\,><\':;|_~`+=)',
									},
								}}
								label='Password'
								placeholder='Enter your password'
								type='password'
							/>
							<MVPTextInput
								formField='referral'
								label='Referral Code'
								placeholder='Enter a referral code (optional)'
								type='text'
								defaultValue={referralCode ?? undefined}
							/>
							<div className='terms-box'>
								<input type='checkbox' className='terms-checkbox' name='terms-box' required />
								<span>
									I agree with the{' '}
									<a href='https://mvppoker.com/terms.html' target='_blank' rel='noreferrer noopener'>
										Terms & Conditions
									</a>
								</span>
							</div>
							<div className='terms-box'>
								<input type='checkbox' className='terms-checkbox' name='terms-box' required />
								<span>
									By checking this box, you acknowledge and certify that you are of legal age<br/>(according to the regulations & requirements of your country) to play poker.
								</span>
							</div>
						</div>
						<MVPButton id='confirm-button' type='submit' loading={isLoading}>
							Register
						</MVPButton>
						<MVPLink className='link-login' onClick={goToLoginPage}>
							Already have an account? <b>Sign in here</b>
						</MVPLink>
					</form>
				</FormProvider>
			</div>
		</div>
	)
}
